import { z } from "zod";
import type { Database } from "./database";
import type { Domain } from "./domain";
import type { DistributionItem } from "@/lib/utils";

/** Serialized filters for dashboard `domains` query + `get_dashboard_aggregates` RPC. */
export const dashboardQueryFiltersSchema = z.object({
  geo: z.array(z.string()).default([]),
  siteTypes: z.array(z.string()).default([]),
  status: z.array(z.string()).default([]),
  languages: z.array(z.string()).default([]),
  keywords: z.array(z.string()).default([]),
  from: z.string().nullable().default(null),
  to: z.string().nullable().default(null),
});

export type DashboardQueryFilters = z.infer<typeof dashboardQueryFiltersSchema>;

export const hasActiveDashboardQueryFilters = (
  filters: DashboardQueryFilters,
): boolean =>
  filters.geo.length > 0 ||
  filters.siteTypes.length > 0 ||
  filters.status.length > 0 ||
  filters.languages.length > 0 ||
  filters.keywords.length > 0 ||
  filters.from != null ||
  filters.to != null;

/** Map stored FilterState / legacy localStorage shape to query filter fields. */
export const normalizeStoredDashboardFilters = (
  raw: unknown,
): unknown => {
  if (!raw || typeof raw !== "object") return raw;
  const o = raw as Record<string, unknown>;
  const dateRange =
    o.dateRange && typeof o.dateRange === "object"
      ? (o.dateRange as Record<string, unknown>)
      : null;

  const toIso = (value: unknown): string | null =>
    typeof value === "string" ? value : null;

  return {
    geo: o.geo,
    siteTypes: o.siteTypes,
    status: o.status,
    languages: o.languages,
    keywords: o.keywords,
    from: toIso(o.from) ?? toIso(dateRange?.from),
    to: toIso(o.to) ?? toIso(dateRange?.to),
  };
};

export const dashboardQueryFiltersToSearchParams = (
  filters: DashboardQueryFilters,
  base?: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams(base);

  const setOrDelete = (key: string, values: string[]) => {
    if (values.length > 0) params.set(key, values.join(","));
    else params.delete(key);
  };

  setOrDelete("geo", filters.geo);
  setOrDelete("siteTypes", filters.siteTypes);
  setOrDelete("status", filters.status);
  setOrDelete("languages", filters.languages);
  setOrDelete("keywords", filters.keywords);

  if (filters.from) params.set("from", filters.from);
  else params.delete("from");

  if (filters.to) params.set("to", filters.to);
  else params.delete("to");

  params.set("page", "1");
  return params;
};

/** Alias matching common naming in loaders / forms */
export const DashboardFiltersSchema = dashboardQueryFiltersSchema;

const parseCommaSeparated = (param: string | null): string[] =>
  param ? param.split(",").map((s) => s.trim()).filter(Boolean) : [];

/** Build validated filters from dashboard URL search params. */
export const parseDashboardFiltersFromUrl = (url: URL): DashboardQueryFilters =>
  dashboardQueryFiltersSchema.parse({
    geo: parseCommaSeparated(url.searchParams.get("geo")),
    siteTypes: parseCommaSeparated(url.searchParams.get("siteTypes")),
    status: parseCommaSeparated(url.searchParams.get("status")),
    languages: parseCommaSeparated(url.searchParams.get("languages")),
    keywords: parseCommaSeparated(url.searchParams.get("keywords")),
    from: url.searchParams.get("from"),
    to: url.searchParams.get("to"),
  });

export type GetDashboardAggregatesArgs =
  Database["public"]["Functions"]["get_dashboard_aggregates"]["Args"];

/** Map UI/query filters to Supabase RPC argument shape. */
export const toGetDashboardAggregatesArgs = (
  filters: DashboardQueryFilters,
  team?: string,
): GetDashboardAggregatesArgs => ({
  p_geo: filters.geo.length > 0 ? filters.geo : undefined,
  p_site_types: filters.siteTypes.length > 0 ? filters.siteTypes : undefined,
  p_status: filters.status.length > 0 ? filters.status : undefined,
  p_languages: filters.languages.length > 0 ? filters.languages : undefined,
  p_keywords: filters.keywords.length > 0 ? filters.keywords : undefined,
  p_from: filters.from ?? undefined,
  p_to: filters.to ?? undefined,
  ...(team ? { p_team: team } : {}), 
});

const distributionCountsSchema = z.record(z.string(), z.number());

/** `get_dashboard_aggregates` JSON payload (`Returns: Json`). */
export const dashboardStatsSchema = z.object({
  total: z.number(),
  live: z.number(),
  jetsikLive: z.number(),
  otherLive: z.number(),
  newDomains: z.number(),
  bought: z.number(),
});

export const dashboardAggregatesResponseSchema = z.object({
  stats: dashboardStatsSchema,
  distributions: z.object({
    siteTypes: distributionCountsSchema,
    geo: distributionCountsSchema,
    keywords: distributionCountsSchema,
    languages: distributionCountsSchema,
  }),
});

export type DashboardAggregatesResponse = z.infer<
  typeof dashboardAggregatesResponseSchema
>;
export type StatsData = z.infer<typeof dashboardStatsSchema>;

export interface DateRangeState {
  from: Date | undefined;
  to: Date | undefined;
}

/** Filter panel state (includes date UI fields not sent as flat RPC args). */
export interface FilterState {
  keywords: string[];
  geo: string[];
  siteTypes: string[];
  status: string[];
  languages: string[];
  dateRange: DateRangeState;
  datePreset: string | null;
}

export interface DashboardDistributions {
  siteTypes: DistributionItem[];
  geo: DistributionItem[];
  keywords: DistributionItem[];
  languages: DistributionItem[];
}

export interface DashboardData {
  domains: Domain[];
  totalCount: number;
  page: number;
  stats: StatsData;
  distributions: DashboardDistributions;
}

export interface DashboardLoaderData {
  page: number;
  limit: number;
  filters: DashboardQueryFilters;
}

export const EMPTY_DASHBOARD_STATS: StatsData = {
  total: 0,
  live: 0,
  jetsikLive: 0,
  otherLive: 0,
  newDomains: 0,
  bought: 0,
};

export const EMPTY_DASHBOARD_DISTRIBUTIONS: DashboardDistributions = {
  siteTypes: [],
  geo: [],
  keywords: [],
  languages: [],
};

export const EMPTY_DOMAINS: Domain[] = [];
